import { certificationApi } from '@/lib/certification-api';
import {
  CreateQuizData,
  GetQuizData,
  QuizQuestionData,
  ICourseRepository,
  IQuizRepository,
  IUserRepository,
} from '../repository/interfaces';
import { IQuizUseCase } from './interfaces';

/**
 * Quiz/Examination Use Case
 * Handles quiz creation, taking, grading, and management
 * User Stories: 13, 14
 */
export class QuizUseCase  implements IQuizUseCase {
  constructor(
    private quizRepo: IQuizRepository,
    private userRepo: IUserRepository,
    private courseRepo: ICourseRepository
  ) {
    // Removed BaseUseCase
  }

  /**
   * Create a quiz with questions
   * User Story 13: As a creator, I add questions (MCQ, short answer, file upload), assign to a quiz, and link quiz to a module
   */
  async createQuizWithQuestions(
    quizData: CreateQuizData,
    questions: Omit<QuizQuestionData, 'id' | 'quizId' | 'createdAt' | 'updatedAt'>[]
  ): Promise<{
    quiz: GetQuizData;
    questions: QuizQuestionData[];
  }> {
    console.log('[QuizUseCase.createQuizWithQuestions] START - Quiz:', quizData.title, 'Questions:', questions.length);
    try {
      console.log('[QuizUseCase.createQuizWithQuestions] Verifying course permissions');
      // Verify creator has permission (is instructor of the course)
      if (quizData.courseId) {
        console.log('[QuizUseCase.createQuizWithQuestions] Checking course:', quizData.courseId);
        const course = await this.courseRepo.getCourseById(quizData.courseId);
        if (!course) {
          console.log('[QuizUseCase.createQuizWithQuestions] ERROR: Course not found');
          throw new Error('Course not found');
        }
        console.log('[QuizUseCase.createQuizWithQuestions] Course found:', course.title);

        // In a real implementation, you'd check if the current user is the instructor
        // For now, we'll assume permission is granted
      }

      console.log('[QuizUseCase.createQuizWithQuestions] Creating quiz');
      // Create the quiz
      const quiz = await this.quizRepo.createQuiz(quizData);
      console.log('[QuizUseCase.createQuizWithQuestions] Quiz created, ID:', quiz.id);

      console.log('[QuizUseCase.createQuizWithQuestions] Adding', questions.length, 'questions');
      // Add questions to the quiz
      const createdQuestions: QuizQuestionData[] = [];
      for (const questionData of questions) {
        const question = await this.quizRepo.addQuizQuestion({
          ...questionData,
          quizId: quiz.id
        });
        createdQuestions.push(question);
      }
      console.log('[QuizUseCase.createQuizWithQuestions] Questions added:', createdQuestions.length);

      console.log('[QuizUseCase.createQuizWithQuestions] Updating quiz question count');
      // Update quiz question count
      await this.quizRepo.updateQuiz(quiz.id, {
        questionCount: createdQuestions.length
      });

      const result = {
        quiz: { ...quiz, questionCount: createdQuestions.length },
        questions: createdQuestions
      };
      console.log('[QuizUseCase.createQuizWithQuestions] SUCCESS - Quiz created with', createdQuestions.length, 'questions');
      return result;
    } catch (error) { 
      console.error('[QuizUseCase.createQuizWithQuestions] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Get quiz with questions for taking
   * User Story 14: As a student, I take quizzes with time limits, see results/feedback per policy
   */
  async getQuizForTaking(
    quizId: string,
    _userId: string // the server identifies the caller from their auth token
  ): Promise<{
    quiz: GetQuizData;
    questions: QuizQuestionData[];
    canTake: boolean;
    reason?: string;
    previousAttempts?: any[];
  }> {
    // From the server: students can't read quiz questions (they carry the
    // answer key), so this returns them with the key stripped.
    const data = await certificationApi.getQuizForTaking(quizId);
    return {
      quiz: { ...(data.quiz as unknown as GetQuizData), timeLimit: data.quiz.timeLimit ?? undefined } as GetQuizData,
      questions: data.questions as unknown as QuizQuestionData[],
      canTake: data.canTake,
      reason: data.reason,
      previousAttempts: data.previousAttempts,
    };
  }

  /**
   * Submit quiz answers and grade
   * User Story 14: As a student, I take quizzes with time limits, see results/feedback per policy
   */
  async submitQuizAnswers(
    quizId: string,
    userId: string,
    answers: Record<string, any>,
    timeTakenSeconds?: number
  ): Promise<{
    score: number;
    totalQuestions: number;
    correctAnswers: number;
    passed: boolean;
    feedback: Array<{
      questionId: string;
      correct: boolean;
      explanation?: string;
    }>;
    attempt: any;
    timeExceeded: boolean;
  }> {
    // Graded on the server (the only kind of attempt course completion counts).
    const result = await certificationApi.submitQuizAttempt(quizId, answers, timeTakenSeconds || 0);
    return {
      score: result.score,
      totalQuestions: result.totalQuestions,
      correctAnswers: result.correctAnswers,
      passed: result.passed,
      feedback: result.results.map((r) => ({ questionId: r.questionId, correct: r.isCorrect })),
      attempt: { id: result.attemptId, userId, quizId, score: result.score, passed: result.passed },
      timeExceeded: result.timedOut,
    };
  }

  /**
   * Get quiz results for student
   */
  async getQuizResults(
    quizId: string,
    userId: string,
    attemptId?: string
  ): Promise<{
    quiz: GetQuizData;
    attempt: any;
    questions: QuizQuestionData[];
    answers: Record<string, any>;
    score: number;
    feedback: Array<{
      questionId: string;
      userAnswer: any;
      correctAnswer: any;
      correct: boolean;
      explanation?: string;
    }>;
  }> {
    console.log('[QuizUseCase.getQuizResults] START - QuizId:', quizId, 'UserId:', userId, 'AttemptId:', attemptId);
    try {
      console.log('[QuizUseCase.getQuizResults] Getting quiz and attempt');
      // Get quiz and attempt
      const quiz = await this.quizRepo.getQuizById(quizId);
      if (!quiz) {
        console.log('[QuizUseCase.getQuizResults] ERROR: Quiz not found');
        throw new Error('Quiz not found');
      }
      console.log('[QuizUseCase.getQuizResults] Found quiz:', quiz.title);

      const attempts = await this.quizRepo.getQuizAttemptsByUser(userId, quizId);
      console.log('[QuizUseCase.getQuizResults] Found', attempts.length, 'attempts');
      const attempt = attemptId
        ? attempts.find(a => a.id === attemptId)
        : attempts[attempts.length - 1]; // Get latest attempt

      if (!attempt) {
        console.log('[QuizUseCase.getQuizResults] ERROR: No attempt found');
        throw new Error('No attempt found');
      }
      console.log('[QuizUseCase.getQuizResults] Using attempt with score:', attempt.score);

      console.log('[QuizUseCase.getQuizResults] Getting questions');
      // Get questions
      const questions = await this.quizRepo.getQuizQuestions(quizId);
      console.log('[QuizUseCase.getQuizResults] Found', questions.length, 'questions');

      console.log('[QuizUseCase.getQuizResults] Generating mock answers for demo');
      // In a real implementation, you'd store user answers with the attempt
      // For demo purposes, we'll create mock answers
      const mockAnswers: Record<string, any> = {};
      questions.forEach(q => {
        if (q.type === 'multipleChoice') {
          mockAnswers[q.id] = q.options ? q.options[0] : 'A';
        } else if (q.type === 'trueFalse') {
          mockAnswers[q.id] = true;
        } else {
          mockAnswers[q.id] = 'Sample answer';
        }
      });

      console.log('[QuizUseCase.getQuizResults] Generating feedback');
      // Generate feedback
      const feedback = questions.map(question => ({
        questionId: question.id,
        userAnswer: mockAnswers[question.id],
        correctAnswer: question.correctAnswer,
        correct: Math.random() > 0.5, // Mock correctness
        explanation: question.explanation
      }));
      console.log('[QuizUseCase.getQuizResults] Generated feedback for', feedback.length, 'questions');

      const result = {
        quiz,
        attempt,
        questions,
        answers: mockAnswers,
        score: attempt.score,
        feedback
      };
      console.log('[QuizUseCase.getQuizResults] SUCCESS - Score:', attempt.score, 'Questions:', questions.length, 'Feedback items:', feedback.length);
      return result;
    } catch (error) { 
      console.error('[QuizUseCase.getQuizResults] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Get all quizzes for a course
   */
  async getCourseQuizzes(
    courseId: string,
    userId?: string
  ): Promise<{
    quizzes: GetQuizData[];
    userAttempts?: Record<string, any[]>;
  }> {
    console.log('[QuizUseCase.getCourseQuizzes] START - CourseId:', courseId, 'UserId:', userId);
    try {
      console.log('[QuizUseCase.getCourseQuizzes] Getting course quizzes');
      const quizzes = await this.quizRepo.getQuizzesByCourse(courseId);
      console.log('[QuizUseCase.getCourseQuizzes] Found', quizzes.length, 'quizzes');

      let userAttempts: Record<string, any[]> | undefined;
      if (userId) {
        console.log('[QuizUseCase.getCourseQuizzes] Getting user attempts for each quiz');
        userAttempts = {};
        for (const quiz of quizzes) {
          const attempts = await this.quizRepo.getQuizAttemptsByUser(userId, quiz.id);
          userAttempts[quiz.id] = attempts;
        }
        console.log('[QuizUseCase.getCourseQuizzes] Retrieved attempts for', Object.keys(userAttempts).length, 'quizzes');
      }

      const result = {
        quizzes,
        userAttempts
      };
      console.log('[QuizUseCase.getCourseQuizzes] SUCCESS - Quizzes:', quizzes.length, 'User attempts:', userId ? 'included' : 'not requested');
      return result;
    } catch (error) { 
      console.error('[QuizUseCase.getCourseQuizzes] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Update quiz questions
   */
  async updateQuizQuestions(
    quizId: string,
    questions: Array<{
      id?: string;
      data: Omit<QuizQuestionData, 'id' | 'quizId' | 'createdAt' | 'updatedAt'>;
    }>
  ): Promise<{
    quiz: GetQuizData;
    questions: QuizQuestionData[];
  }> {
    console.log('[QuizUseCase.updateQuizQuestions] START - QuizId:', quizId, 'Questions:', questions.length);
    try {
      console.log('[QuizUseCase.updateQuizQuestions] Getting existing questions');
      // Get existing questions
      const existingQuestions = await this.quizRepo.getQuizQuestions(quizId);
      console.log('[QuizUseCase.updateQuizQuestions] Found', existingQuestions.length, 'existing questions');

      console.log('[QuizUseCase.updateQuizQuestions] Deleting existing questions');
      // Delete existing questions
      await this.quizRepo.deleteQuizQuestions(quizId);

      console.log('[QuizUseCase.updateQuizQuestions] Adding', questions.length, 'new questions');
      // Add new questions
      const createdQuestions: QuizQuestionData[] = [];
      for (const questionInput of questions) {
        const question = await this.quizRepo.addQuizQuestion({
          ...questionInput.data,
          quizId: quizId
        });
        createdQuestions.push(question);
      }
      console.log('[QuizUseCase.updateQuizQuestions] Created', createdQuestions.length, 'questions');

      console.log('[QuizUseCase.updateQuizQuestions] Updating quiz question count');
      // Update quiz question count
      const updatedQuiz = await this.quizRepo.updateQuiz(quizId, {
        questionCount: createdQuestions.length
      });

      const result = {
        quiz: updatedQuiz,
        questions: createdQuestions
      };
      console.log('[QuizUseCase.updateQuizQuestions] SUCCESS - Updated quiz with', createdQuestions.length, 'questions');
      return result;
    } catch (error) { 
      console.error('[QuizUseCase.updateQuizQuestions] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Delete quiz and all its data
   */
  async deleteQuiz(quizId: string): Promise<void> {
    console.log('[QuizUseCase.deleteQuiz] START - QuizId:', quizId);
    try {
      console.log('[QuizUseCase.deleteQuiz] Deleting quiz questions');
      // Delete questions
      await this.quizRepo.deleteQuizQuestions(quizId);

      console.log('[QuizUseCase.deleteQuiz] Deleting quiz attempts');
      // Delete attempts
      await this.quizRepo.deleteQuizAttempts(quizId);

      console.log('[QuizUseCase.deleteQuiz] Deleting quiz');
      // Delete quiz
      await this.quizRepo.deleteQuiz(quizId);

      console.log(`[QuizUseCase.deleteQuiz] Quiz ${quizId} and all associated data deleted`);
      console.log('[QuizUseCase.deleteQuiz] SUCCESS - Quiz completely deleted');
    } catch (error) { 
      console.error('[QuizUseCase.deleteQuiz] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Get quiz statistics for instructor
   */
  async getQuizStatistics(
    quizId: string
  ): Promise<{
    quiz: GetQuizData;
    totalAttempts: number;
    averageScore: number;
    passRate: number;
    attemptsByScore: Array<{
      range: string;
      count: number;
    }>;
  }> {
    console.log('[QuizUseCase.getQuizStatistics] START - QuizId:', quizId);
    try {
      console.log('[QuizUseCase.getQuizStatistics] Getting quiz details');
      const quiz = await this.quizRepo.getQuizById(quizId);
      if (!quiz) {
        console.log('[QuizUseCase.getQuizStatistics] ERROR: Quiz not found');
        throw new Error('Quiz not found');
      }
      console.log('[QuizUseCase.getQuizStatistics] Found quiz:', quiz.title);

      console.log('[QuizUseCase.getQuizStatistics] Getting quiz attempts');
      // Get all attempts for this quiz
      // Note: In a real implementation, you'd have a method to get all attempts for a quiz
      // For demo, we'll use a simplified approach
      const allAttempts = []; // Would be fetched from repository
      console.log('[QuizUseCase.getQuizStatistics] Found', allAttempts.length, 'attempts');

      const totalAttempts = allAttempts.length;
      const averageScore = totalAttempts > 0
        ? allAttempts.reduce((sum, attempt) => sum + attempt.score, 0) / totalAttempts
        : 0;

      const passedAttempts = allAttempts.filter(attempt => attempt.score >= (quiz.passingScore || 70));
      const passRate = totalAttempts > 0 ? (passedAttempts.length / totalAttempts) * 100 : 0;
      console.log('[QuizUseCase.getQuizStatistics] Pass rate:', Math.round(passRate), '% (', passedAttempts.length, '/', totalAttempts, ')');

      console.log('[QuizUseCase.getQuizStatistics] Calculating score distribution');
      // Score distribution
      const scoreRanges = [
        { range: '0-20', min: 0, max: 20 },
        { range: '21-40', min: 21, max: 40 },
        { range: '41-60', min: 41, max: 60 },
        { range: '61-80', min: 61, max: 80 },
        { range: '81-100', min: 81, max: 100 }
      ];

      const attemptsByScore = scoreRanges.map(range => ({
        range: range.range,
        count: allAttempts.filter(attempt =>
          attempt.score >= range.min && attempt.score <= range.max
        ).length
      }));

      const result = {
        quiz,
        totalAttempts,
        averageScore: Math.round(averageScore * 100) / 100,
        passRate: Math.round(passRate * 100) / 100,
        attemptsByScore
      };
      console.log('[QuizUseCase.getQuizStatistics] SUCCESS - Total attempts:', totalAttempts, 'Average score:', result.averageScore, '%, Pass rate:', result.passRate, '%');
      return result;
    } catch (error) { 
      console.error('[QuizUseCase.getQuizStatistics] ERROR:', error); 
      throw error; 
    }
  }
}
